import React, { useEffect, useRef } from 'react';

import { CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet';
import type { OrgMemberOption } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useCurrentUserQuery } from 'src/gql/hooks/user';
import type { ReactSetStateTuple } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import type { LabelOption } from './board-toolbar';
import { BoardBody } from './BoardBody';

const useStyles = makeStyles()(theme => ({
  board: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    minWidth: 0,
    overflow: 'auto',
    width: '100%',
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
}));

export interface BoardLocalState {
  filterAssignees: OrgMemberOption[];
  filterLabels: LabelOption[];
  searchString: string;
  filterPriorities?: Apollo.WorkflowPriority[];
  filterStates?: Apollo.WorkflowState[];
}

export const initialBoardState: BoardLocalState = {
  filterAssignees: [],
  filterLabels: [],
  filterPriorities: [],
  filterStates: [],
  searchString: '',
};

interface BoardProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  onBoardChange: (boardId: string) => void;
  selectedBoardId: string | undefined;
}

export function Board({
  boardStateTuple,
  onBoardChange,
  selectedBoardId,
}: BoardProps): JSX.Element | null {
  const { classes } = useStyles();
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const { currentUser, loading: currentUserLoading } = useCurrentUserQuery();
  const { data: boardsData, loading: boardsLoading } = Apollo.useGetBoardsQuery();
  const boards = boardsData?.boards ?? [];

  // Derive effective board ID: user selection > org default board > first board
  const orgDefaultBoardId = currentUser?.org?.defaultBoard?.id;
  const effectiveBoardId = selectedBoardId ?? orgDefaultBoardId ?? boards.at(0)?.id;

  useEffect(() => {
    if (selectedBoardId || !effectiveBoardId) return;

    onBoardChange(effectiveBoardId);
  }, [effectiveBoardId, onBoardChange, selectedBoardId]);

  if (!effectiveBoardId && (currentUserLoading || boardsLoading)) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!effectiveBoardId) return null;

  return (
    <>
      <Helmet>
        <title>Workflow Board</title>
      </Helmet>
      <div ref={boardScrollRef} className={classes.board}>
        <BoardBody
          boardStateTuple={boardStateTuple}
          scrollContainerRef={boardScrollRef}
          selectedBoardId={effectiveBoardId}
        />
      </div>
    </>
  );
}
